// ./src/pages/private/EventDrawsPage/DrawRoundDrawer.tsx
//
// Configure a prize-draw round (#30): the prize title, how many winners, optional
// per-rank tiered prizes, whether to exclude earlier winners, the public
// "coming soon" message, and notes. Drawing the winners happens separately
// (DrawRunDrawer). URL-driven (add / :id/edit).

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Stack, Group, Text, Box, Paper, ActionIcon, Divider, Input } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";

import { GuestDrawService, GuestDraw, GuestDrawInput, GuestDrawPrize } from "@/utils/api";
import { getFormErrors } from "@/utils";
import { useFormSnapshot } from "@/hooks";
import { AppDrawer, AppButton, AppIcon, TextField, TextAreaField, NumberField, SwitchField } from "@/components/generic";

export interface DrawRoundDrawerProps {
  opened: boolean;
  onClose: () => void;
  eventId: string;
  /** null = create a new round; otherwise edit this one. */
  draw: GuestDraw | null;
  onSaved: () => void;
}

interface RoundForm {
  name: string;
  description: string;
  winnersCount: number;
  tiered: boolean;
  prizes: GuestDrawPrize[];
  excludePreviousWinners: boolean;
  pendingMessage: string;
  notes: string;
}

const KNOWN_FORM_FIELDS = ["name", "description", "winnersCount"];

const EMPTY_FORM: RoundForm = {
  name: "",
  description: "",
  winnersCount: 1,
  tiered: false,
  prizes: [{ label: "", description: "" }],
  excludePreviousWinners: true,
  pendingMessage: "",
  notes: "",
};

const fromDraw = (d: GuestDraw): RoundForm => {
  const prizes = Array.isArray(d.prizes) ? d.prizes : [];
  const tiered = prizes.length > 0;
  return {
    name: d.name ?? "",
    description: d.description ?? "",
    winnersCount: d.winnersCount ?? 1,
    tiered,
    prizes: tiered ? prizes.map((p) => ({ label: p.label ?? "", description: p.description ?? "" })) : [{ label: "", description: "" }],
    excludePreviousWinners: d.excludePreviousWinners !== false,
    pendingMessage: d.pendingMessage ?? "",
    notes: d.notes ?? "",
  };
};

export const DrawRoundDrawer: React.FC<DrawRoundDrawerProps> = ({ opened, onClose, eventId, draw, onSaved }) => {
  const { t } = useTranslation(["events", "common"]);

  const [form, setForm] = useState<RoundForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  // The submit's OWN OpResult, kept at component scope. Mapping it inside the
  // save handler threw the per-field messages away the moment the handler
  // returned, so a server refusal on these fields was never rendered.
  const [submitError, setSubmitError] = useState<OpResult | null>(null);
  const seededRef = useRef<string | null>(null);

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(submitError, KNOWN_FORM_FIELDS),
    [submitError]
  );

  // Esc / a click outside / Cancel ask before discarding a changed round (#123).
  const { isDirty, seedFrom, clear } = useFormSnapshot(form);

  // Seed once per open (keyed on the round id) so it doesn't clobber edits mid-session.
  useEffect(() => {
    if (!opened) {
      seededRef.current = null;
      clear();

      return;
    }
    const key = draw?.id ?? "__new__";
    if (seededRef.current === key) {
      return;
    }
    seededRef.current = key;
    const seeded = draw ? fromDraw(draw) : EMPTY_FORM;
    setForm(seeded);
    seedFrom(seeded);
    setSubmitError(null);
  }, [opened, draw, clear, seedFrom]);

  const set = useCallback(<K extends keyof RoundForm>(key: K, value: RoundForm[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  }, []);

  const setPrize = useCallback((idx: number, patch: Partial<GuestDrawPrize>) => {
    setForm((f) => ({ ...f, prizes: f.prizes.map((p, i) => (i === idx ? { ...p, ...patch } : p)) }));
  }, []);

  const addPrize = useCallback(() => {
    setForm((f) => ({ ...f, prizes: [...f.prizes, { label: "", description: "" }] }));
  }, []);

  const removePrize = useCallback((idx: number) => {
    setForm((f) => ({ ...f, prizes: f.prizes.length > 1 ? f.prizes.filter((_, i) => i !== idx) : f.prizes }));
  }, []);

  const handleSave = useCallback(async () => {
    setSubmitError(null);

    const cleanPrizes = form.prizes
      .map((p) => ({ label: (p.label ?? "").trim(), description: (p.description ?? "").trim() || null }))
      .filter((p) => p.label);

    const winnersCount = form.tiered ? Math.max(1, cleanPrizes.length) : Math.max(1, form.winnersCount || 1);

    const params: GuestDrawInput = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      winnersCount,
      prizes: form.tiered ? cleanPrizes : null,
      excludePreviousWinners: form.excludePreviousWinners,
      pendingMessage: form.pendingMessage.trim() || null,
      notes: form.notes.trim() || null,
    };

    setSaving(true);
    const result = draw?.id
      ? await GuestDrawService.update(draw.id, params)
      : await GuestDrawService.create({ ...params, eventId });
    setSaving(false);

    if (result.code === OP_RESULT_CODES.OK) {
      onSaved();
      onClose();

      return;
    }

    // Keep the OpResult: field messages go under their inputs, the rest above Save.
    setSubmitError(result);
  }, [form, draw, eventId, onSaved, onClose]);

  // `getFormErrors` pre-fills every known field with undefined, so a keys check
  // would always be true — only a truthy VALUE means a field is flagged.
  const hasFieldErrors = Object.values(fieldErrors).some(Boolean);

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={draw ? t("events:draws.editRound", { defaultValue: "Edit prize draw" }) : t("events:draws.addRound", { defaultValue: "Add prize draw" })}
      size="lg"
      loading={saving}
      errors={genericErrors}
      hasFieldErrors={hasFieldErrors}
      actionType="save-cancel"
      confirmShortcut="⌘S"
      onConfirm={handleSave}
      isDirty={isDirty}
    >
      <Stack gap="md">
        <TextField
          name="name"
          label={t("events:draws.nameLabel", { defaultValue: "Prize / round name" })}
          placeholder={t("events:draws.namePlaceholder", { defaultValue: "e.g. Grand Prize" })}
          value={form.name}
          onChange={(_n, v) => set("name", v ?? "")}
          error={fieldErrors.name}
          required
          data-autofocus
        />

        <TextAreaField
          name="description"
          label={t("events:draws.descriptionLabel", { defaultValue: "Description" })}
          placeholder={t("events:draws.descriptionPlaceholder", { defaultValue: "What is this draw for?" })}
          value={form.description}
          onChange={(_n, v) => set("description", v ?? "")}
          autosize
          minRows={2}
          error={fieldErrors.description}
        />

        <SwitchField
          name="tiered"
          label={t("events:draws.tieredLabel", { defaultValue: "Different prize per place" })}
          description={t("events:draws.tieredHint", { defaultValue: "Give 1st / 2nd / 3rd… their own prize. Off = one prize, N winners." })}
          checked={form.tiered}
          onChange={(_n, v) => set("tiered", v)}
        />

        {form.tiered ? (
          <Box>
            <Text size="md" fw={600} mb={6}>
              {t("events:draws.prizesLabel", { defaultValue: "Prizes by place" })}
            </Text>
            <Stack gap="xs">
              {form.prizes.map((p, i) => (
                <Paper key={i} withBorder radius="md" p="sm">
                  <Group align="flex-start" gap="xs" wrap="nowrap">
                    <Text size="md" fw={600} c="dimmed" w={28} ta="center" mt={8}>
                      {i + 1}
                    </Text>
                    <Stack gap={6} style={{ flex: 1 }}>
                      <TextField
                        name={`prize-${i}`}
                        placeholder={t("events:draws.prizeLabelPh", { defaultValue: "Prize for this place, e.g. iPad" })}
                        value={p.label ?? ""}
                        onChange={(_n, v) => setPrize(i, { label: v ?? "" })}
                      />
                      <TextField
                        name={`prize-desc-${i}`}
                        placeholder={t("events:draws.prizeDescPh", { defaultValue: "Details (optional)" })}
                        value={p.description ?? ""}
                        onChange={(_n, v) => setPrize(i, { description: v ?? "" })}
                      />
                    </Stack>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      mt={6}
                      disabled={form.prizes.length <= 1}
                      onClick={() => removePrize(i)}
                      aria-label={t("common:buttons.remove", { defaultValue: "Remove" })}
                    >
                      <AppIcon iconName="Trash" size={16} />
                    </ActionIcon>
                  </Group>
                </Paper>
              ))}
            </Stack>
            <AppButton variant="secondary" iconName="Plus" mt="xs" onClick={addPrize}>
              {t("events:draws.addPrize", { defaultValue: "Add place" })}
            </AppButton>
            <Text size="sm" c="dimmed" mt={6}>
              {t("events:draws.tieredCount", { defaultValue: "{{count}} winner(s) will be drawn — one per place.", count: form.prizes.filter((p) => (p.label ?? "").trim()).length || form.prizes.length })}
            </Text>

            {/* With tiered prizes the winner count comes from the places, so a server
                refusal on `winnersCount` belongs on this block — it has no input of its own here. */}
            {fieldErrors.winnersCount ? <Input.Error mt={6}>{fieldErrors.winnersCount}</Input.Error> : null}
          </Box>
        ) : (
          <NumberField
            name="winnersCount"
            label={t("events:draws.winnersCountLabel", { defaultValue: "Number of winners" })}
            value={form.winnersCount}
            onChange={(_n, v) => set("winnersCount", typeof v === "number" && v > 0 ? v : 1)}
            min={1}
            max={1000}
            error={fieldErrors.winnersCount}
          />
        )}

        <SwitchField
          name="excludePreviousWinners"
          label={t("events:draws.excludeLabel", { defaultValue: "Exclude earlier winners" })}
          description={t("events:draws.excludeHint", { defaultValue: "Skip guests who already won another draw for this event." })}
          checked={form.excludePreviousWinners}
          onChange={(_n, v) => set("excludePreviousWinners", v)}
        />

        <Divider label={t("events:draws.websiteSection", { defaultValue: "Event website" })} labelPosition="left" />

        <TextAreaField
          name="pendingMessage"
          label={t("events:draws.pendingLabel", { defaultValue: "“Coming soon” message" })}
          description={t("events:draws.pendingHint", { defaultValue: "Shown on the website before this round is drawn / revealed. Leave blank to use the block’s default." })}
          placeholder={t("events:draws.pendingPh", { defaultValue: "Winners announced live at 9pm!" })}
          value={form.pendingMessage}
          onChange={(_n, v) => set("pendingMessage", v ?? "")}
          autosize
          minRows={2}
        />

        <TextAreaField
          name="notes"
          label={t("events:draws.notesLabel", { defaultValue: "Notes (private)" })}
          value={form.notes}
          onChange={(_n, v) => set("notes", v ?? "")}
          autosize
          minRows={2}
        />
      </Stack>
    </AppDrawer>
  );
};
